﻿import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import CustomInfoMessage from "@/components/desktop/list/CustomInfoMessage";
import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";

import useListController from "@/hooks/list/useListController";
import { useScreenPermissions } from "@/hooks/general/useScreenPermissions";

import useFilterStore from "@/stores/useFilterStore";

import { useCategories, useDeleteCategory } from "./hooks/useCategory";
import { useCategoryColumns } from "./hooks/useCategoryColumns";

import { base_table } from "./categories.constants";
import { SCREEN_ID_CATEGORIES } from "@/constants/screenIds";

import CategoryForm from "./Form/CategoryForm";

const Categories = () => {
	const navigate = useNavigate();
	const { filter } = useFilterStore();
	const { estate_parent_id, estate_id, search, parameter } = filter;

	const { canShow, canCreate, canEdit, canDelete, isLoading: isLoadingPermissions } = useScreenPermissions(SCREEN_ID_CATEGORIES);
	const canLoadScreenData = !isLoadingPermissions && canShow;

	const { tableState, appliedFilterParams, reactTableSort, handlePageChange, handlePageSizeChange, handleSortByChange } = useListController({
		baseFilter: [],
		baseTable: base_table,
	});

	const { data, isFetching, isLoading } = useCategories(
		{
			estate_id,
			estate_parent_id,
			search,
			parameter,
			...appliedFilterParams,
			...tableState,
		},
		canLoadScreenData,
	);

	const { mutate: deleteCategory, isLoading: isLoadingDelete } = useDeleteCategory();

	const [showDelete, setShowDelete] = useState(false);
	const [selected, setSelected] = useState(null);
	const [infoMessages, setInfoMessages] = useState([]);
	const [messageType] = useState("info");

	const handleDelete = () => {
		setShowDelete(false);

		deleteCategory(
			{
				selected,
				filters: {
					estate_id,
					estate_parent_id,
					search,
					parameter,
					...appliedFilterParams,
					...tableState,
				},
			},
			{
				onSuccess: () => {
					toast.success("Categoría eliminada correctamente");
				},
				onError: () => {
					toast.error("Error al eliminar categoría");
				},
			},
		);
	};

	const columns = useCategoryColumns({
		tableState,
		navigate,
		isLoadingSave: false,
		selected,
		setSelected,
		setShowDelete,
		canEdit,
		canDelete,
	});

	if (isLoadingPermissions) return <CustomFetching center message="Validando permisos..." />;
	if (!canShow) return <CustomNoPermissionView />;

	return (
		<ListScaffold
			titleBreadcrumbs={[
				{ label: "Dashboard", onClick: () => navigate("/") },
				{ label: "Configuración", onClick: () => navigate("/configuracion") },
				{ label: "Categorías", onClick: () => navigate("/configuracion/categorias") },
			]}
			subtitle="Gestione las categorías del sistema."
			showCreate={canCreate}
			createLabel="Nueva categoría"
			onCreate={canCreate ? () => navigate("/configuracion/categorias/crear") : undefined}
			isFetching={isFetching}
			isLoading={isLoading || isLoadingDelete}
			data={data?.data || []}
			totalItems={data?.total || 0}
			totalPages={data?.last_page || 0}
			toItem={data?.to || 0}
			fromItem={data?.from || 0}
			pageIndex={tableState.page}
			columns={columns}
			onPageChange={handlePageChange}
			pageSize={tableState.per_page}
			onPageSizeChange={handlePageSizeChange}
			sortBy={reactTableSort}
			onSortByChange={handleSortByChange}
			pageSizeOptions={[20, 50, 100]}
			initialPageSize={tableState.per_page}
			initialSortBy={reactTableSort}
			topContent={
				infoMessages.length > 0 && (
					<CustomInfoMessage messages={infoMessages} type={messageType} autoCloseDuration={30000} onClose={() => setInfoMessages([])} />
				)
			}
		>
			<CategoryForm
				filters={{
					estate_id,
					estate_parent_id,
					search,
					parameter,
					...appliedFilterParams,
					...tableState,
				}}
			/>

			<BasicConfirm
				showConfirm={showDelete}
				setShowConfirm={setShowDelete}
				handleAction={handleDelete}
				showValidation={true}
				textValidation={"eliminar categoría"}
				subMessage="Esta acción eliminará permanentemente la categoría seleccionada."
			/>
		</ListScaffold>
	);
};

export default Categories;
